import { clsx } from "clsx";
import { Component } from "react";
import { connect } from "react-redux";

import { ActionButton, ActionButtonIntent, ActionButtonSize, Callout, CalloutIntent, SymbolIcon } from "widgets";

import style from "../../../styles/callout.module.scss";

import { ContentPlaceholder } from "../../core/ContentPlaceholder.js";
import FileDropzone from "../../core/forms/FileDropzone.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";
import { getFormError, getFormWarning } from "@/app-utils/form.ts";
import { type Playbook } from "@/graphql.ts";
import { FileUploadTile } from "@/modules/files/components/FileUploadTile.js";
import {
  type IPlaybookConfigState,
  downloadPlaybookTemplateResource,
  generatePlaybookTemplateResource,
  updatePlaybookConfigState,
} from "@/redux/modules/playbook-admin.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { FILE_STATUS, PLAYBOOK_REPORT_TEMPLATE_RESOURCES } from "@/settings/settings.ts";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputHint } from "@/ui-modules/core/form/layout/FormInputHint.tsx";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";

type IPlaybookConfigReportScreenOwnProps = IPlaybookWizardStepProvidedProps;

interface IPlaybookConfigReportScreenAppStateProps {
  playbookCreationState: Partial<IPlaybookConfigState>;
  globalFormError: string;
  globalFormWarning: string;
}

interface IPlaybookConfigReportScreenDispatchProps {
  updatePlaybookCreationState: (state: Partial<IPlaybookConfigState>) => void;
  downloadPlaybookTemplateResource: (
    playbook: Playbook,
    resourceType: PLAYBOOK_REPORT_TEMPLATE_RESOURCES,
  ) => Promise<any>;
  generatePlaybookTemplateResource: (
    sampleTypeId: number,
    playbookTypeId: number,
    resourceType: PLAYBOOK_REPORT_TEMPLATE_RESOURCES,
  ) => Promise<any>;
}

export interface IPlaybookConfigReportScreenProps
  extends IPlaybookConfigReportScreenAppStateProps,
    IPlaybookConfigReportScreenDispatchProps,
    IPlaybookConfigReportScreenOwnProps,
    IPlaybookWizardStepInjectedProps {
  // only if this screen is a redux-form
}

function mapStateToProps(
  state: IAppState,
  _ownProps: IPlaybookConfigReportScreenProps,
): IPlaybookConfigReportScreenAppStateProps {
  return {
    playbookCreationState: state.playbookAdminState.playbookConfigState,
    globalFormError: getFormError(PlaybookConfigReportScreen.PLAYBOOK_CREATION_REPORT_SCREEN, state),
    globalFormWarning: getFormWarning(PlaybookConfigReportScreen.PLAYBOOK_CREATION_REPORT_SCREEN, state),
  };
}

function mapDispatchToProps(
  dispatch,
  _ownProps: IPlaybookConfigReportScreenOwnProps,
): IPlaybookConfigReportScreenDispatchProps {
  return {
    updatePlaybookCreationState: (state: Partial<IPlaybookConfigState>) => dispatch(updatePlaybookConfigState(state)),
    downloadPlaybookTemplateResource: (playbook: Playbook, resourceType: PLAYBOOK_REPORT_TEMPLATE_RESOURCES) =>
      dispatch(downloadPlaybookTemplateResource(playbook, resourceType)),
    generatePlaybookTemplateResource: (
      sampleTypeId: number,
      playbookTypeId: number,
      resourceType: PLAYBOOK_REPORT_TEMPLATE_RESOURCES,
    ) => dispatch(generatePlaybookTemplateResource(sampleTypeId, playbookTypeId, resourceType)),
  };
}

interface IPlaybookConfigReportScreenState {
  reportTemplateResources: File[];
  formError: string;
  fileWarning: string;
  showAdditionalInformation: boolean;
}

class PlaybookConfigReportScreen extends Component<IPlaybookConfigReportScreenProps, IPlaybookConfigReportScreenState> {
  public static PLAYBOOK_CREATION_REPORT_SCREEN = "PLAYBOOK_CREATION_REPORT_SCREEN";
  private dropZoneOpenCallback = null;

  constructor(props) {
    super(props);
    this.state = {
      reportTemplateResources: [],
      showAdditionalInformation: false,
      formError: null,
      fileWarning: null,
    };
  }

  componentDidMount(): void {
    const existingResources: File[] = this.props.playbookCreationState.reportTemplateResources?.map(
      this.addTypeToReportTemplateResource,
    );
    this.setState({ reportTemplateResources: existingResources || [] });
  }

  validationErrorMessage = () => {
    // it is okay to skip this step and leave as is
    if (this.state.reportTemplateResources.length === 0) {
      return;
    }

    if (
      this.state.reportTemplateResources.length !== 2 ||
      !this.state.reportTemplateResources.find(
        (resource: File) => resource.type !== PLAYBOOK_REPORT_TEMPLATE_RESOURCES.TEMPLATE_FILE,
      ) ||
      !this.state.reportTemplateResources.find(
        (resource: File) => resource.type !== PLAYBOOK_REPORT_TEMPLATE_RESOURCES.DESIGN_FILE,
      )
    ) {
      return "You must select one single .fo file and its corresponding .xre file";
    }
  };

  handleSubmitData = () => {
    const errorMessage = this.validationErrorMessage();
    if (errorMessage) {
      this.setState({ formError: errorMessage });
      return;
    }

    this.props.updatePlaybookCreationState({
      reportTemplateResources: this.state.reportTemplateResources,
    });

    this.props.proceedWithNextStep();
  };

  downloadTemplateResource = (resourceType: PLAYBOOK_REPORT_TEMPLATE_RESOURCES) => {
    const playbook: Playbook = this.props.playbookCreationState.initialPlaybook;
    this.props.downloadPlaybookTemplateResource(playbook, resourceType).catch((error: Error) => {
      this.props.updateWizardMessage(`Failed to download the Playbook resource: ${error.message}`);
    });
    this.setState({ showAdditionalInformation: true });
  };

  generateTemplateResource = (resourceType: PLAYBOOK_REPORT_TEMPLATE_RESOURCES) => {
    const sampleTypeId: number = this.props.playbookCreationState.sampleTypeId;
    const playbookTypeId: number = this.props.playbookCreationState.playbookType;
    this.props.generatePlaybookTemplateResource(sampleTypeId, playbookTypeId, resourceType).catch((error: Error) => {
      this.props.updateWizardMessage(`Failed to generate and download the Playbook resource: ${error.message}`);
    });
  };

  render() {
    return (
      <PlaybookWizardStep
        {...this.props}
        playbookPage={this.props.playbookPage}
        wizardFooterData={{
          error: this.props.globalFormError || this.state.formError,
          warning: this.props.globalFormWarning,
          nextButtonProps: {
            label: "Next",
            isLoading: false, // !!this.props.asyncValidating,
            onPress: this.handleSubmitData,
          },
        }}
      >
        <form className={"playbook-report-template-upload"}>
          <p>
            A generic out-of-the-box PDF report will be generated for you and attached to the analyzed samples, but it
            is also possible to customize the report template in order to fit your expectation.
          </p>

          <p>Any PDF reports that are generated by the NGS workflow will be appended to the end of this PDF report.</p>

          <p>
            Start here to customize the current state of the PDF report template with the{" "}
            <a href={"http://www.java4less.com/fopdesigner/fodesigner.php?info=guide"}>FO Designer</a> visual editor
            &nbsp;&nbsp;
            <ActionButton
              onPress={() => {
                this.downloadTemplateResource(PLAYBOOK_REPORT_TEMPLATE_RESOURCES.DESIGN_FILE);
              }}
              icon={"download"}
              label={"Download Report Template Project (.xre)"}
              size={ActionButtonSize.Small}
            />
          </p>

          {this.state.showAdditionalInformation && (
            <Callout intent={CalloutIntent.Warning}>
              <p>
                <b>You will also need additional resources in your FO Designers project:</b>
                <br />
                <SymbolIcon icon={"arrow_right"} />
                &nbsp;&nbsp;the schema file describing the available data&nbsp;&nbsp;
                <ActionButton
                  onPress={() => {
                    this.downloadTemplateResource(PLAYBOOK_REPORT_TEMPLATE_RESOURCES.SCHEMA_FILE);
                  }}
                  label={"Download Schema File (.xsd)"}
                  intent={ActionButtonIntent.Outlined}
                  size={ActionButtonSize.Small}
                  icon={"download"}
                />
                <br />
                <SymbolIcon icon={"arrow_right"} />
                &nbsp;some example of data in order to easily test your customized report template&nbsp;&nbsp;
                <ActionButton
                  onPress={() => {
                    this.generateTemplateResource(PLAYBOOK_REPORT_TEMPLATE_RESOURCES.DATA_EXAMPLE_FILE);
                  }}
                  label={"Download Example Data File (.xml)"}
                  intent={ActionButtonIntent.Outlined}
                  size={ActionButtonSize.Small}
                  icon={"download"}
                />
                <br />
                <SymbolIcon icon={"arrow_right"} />
                &nbsp;the current PDF report template in case you want to modify it on your own&nbsp;&nbsp;
                <ActionButton
                  onPress={() => {
                    this.downloadTemplateResource(PLAYBOOK_REPORT_TEMPLATE_RESOURCES.TEMPLATE_FILE);
                  }}
                  label={"Download Report Template (.fo)"}
                  intent={ActionButtonIntent.Outlined}
                  size={ActionButtonSize.Small}
                  icon={"download"}
                />
              </p>

              <p>
                For more information on the usage of FO Designer, please have a look at the
                <a
                  className={"alert-link"}
                  href={"http://www.java4less.com/fopguide/apexprint.php"}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  &nbsp;online user guide
                </a>
              </p>
            </Callout>
          )}

          <FormInputGroup key="form-group-dropzone">
            <FormLabel>Report template</FormLabel>

            <FormInputWidget>
              <FormInputHint align={"start"}>
                <ActionButton
                  label={"Select and Import Files"}
                  onPress={this.handleFileUploadClicked}
                  size={ActionButtonSize.Small}
                />{" "}
                or drag and drop them in the area below. <em>Choose one .fo file and its corresponding .xre file.</em>
              </FormInputHint>
              <div>
                <FileDropzone
                  setDropZoneOpenCallback={this.setDropZoneOpenCallback}
                  addResources={this.addResources}
                  showFileDropWarningMessage={this.showFileDropWarningMessage}
                  showAlways={true}
                  disabled={false}
                  dropzoneContent={this.getDropzoneContent()}
                  acceptedTypes={{ "Report Template": [".fo", ".xre"] }}
                />
              </div>

              {this.state.fileWarning && <Callout className={clsx(style.oldWarning)}>{this.state.fileWarning}</Callout>}
            </FormInputWidget>
          </FormInputGroup>
        </form>
      </PlaybookWizardStep>
    );
  }

  setDropZoneOpenCallback = (callback) => {
    this.dropZoneOpenCallback = callback;
  };

  handleFileUploadClicked = () => {
    if (this.dropZoneOpenCallback) {
      this.dropZoneOpenCallback();
    }
  };

  getDropzoneContent = () => {
    if (this.state.reportTemplateResources.length) {
      return (
        <div>
          {this.state.reportTemplateResources.map((resource: File, i) => (
            <FileUploadTile
              key={`${i}-${resource.name}`}
              file={{ fileName: resource.name, fileSize: resource.size }}
              uploadStatus={FILE_STATUS.VALIDATION_COMPLETE}
              progress={null}
              errors={null}
              cancelUploadRequest={undefined}
              clearFile={() => this.removeResource(resource.name)}
              hideSize={true}
            />
          ))}
        </div>
      );
    }
    return (
      <ContentPlaceholder
        text={
          <div>
            <SymbolIcon icon={"cloud_upload"} />
            <br />
            Drag and drop your report template files here
          </div>
        }
      />
    );
  };

  showFileDropWarningMessage = (warningMessage: string) => {
    this.setState({ fileWarning: warningMessage });
  };

  addResources = (files: File[]) => {
    const resources = files.map(this.addTypeToReportTemplateResource);
    this.setState({
      fileWarning: null,
      formError: null,
      reportTemplateResources: [...this.state.reportTemplateResources, ...resources],
    });
  };

  addTypeToReportTemplateResource = (resource: File) => {
    if (resource.name.endsWith(".fo")) {
      return new File([resource], resource.name, { type: PLAYBOOK_REPORT_TEMPLATE_RESOURCES.TEMPLATE_FILE });
    }
    if (resource.name.endsWith(".xre")) {
      return new File([resource], resource.name, { type: PLAYBOOK_REPORT_TEMPLATE_RESOURCES.DESIGN_FILE });
    }
    return resource;
  };

  removeResource = (fileName: string) => {
    this.setState({
      fileWarning: null,
      formError: null,
      reportTemplateResources: this.state.reportTemplateResources.filter((resource) => resource.name !== fileName),
    });
  };
}

export default connect<
  IPlaybookConfigReportScreenAppStateProps,
  IPlaybookConfigReportScreenDispatchProps,
  IPlaybookConfigReportScreenOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(PlaybookConfigReportScreen) as any;
